import { Component } from 'react';

import BpkBadge, {
  BADGE_TYPES,
} from '@skyscanner-internal/backpack-web/bpk-component-badge';
import BpkCard from '@skyscanner-internal/backpack-web/bpk-component-card';
import LocationIconSM from '@skyscanner-internal/backpack-web/bpk-component-icon/sm/location';
import BpkLoadingButton from '@skyscanner-internal/backpack-web/bpk-component-loading-button';
import {
  BpkSpinner,
  SPINNER_TYPES,
} from '@skyscanner-internal/backpack-web/bpk-component-spinner';
import BpkText, {
  TEXT_STYLES,
  TEXT_COLORS,
} from '@skyscanner-internal/backpack-web/bpk-component-text';
import { HOTELS_PRICE_TYPE } from '@skyscanner-internal/landing-pages-shared-constants/constants';
import classnames from 'classnames';
import get from 'lodash/get';
import isEqual from 'lodash/isEqual';

import CugBadge from '../../hotels-utils/components-hotel-card-adjacent/CugBadge';
import HotelStars from '../../hotels-utils/components-hotel-card-adjacent/HotelStars';
import OTALogo from '../../hotels-utils/components-hotel-card-adjacent/OTALogo';
// import { mark } from '../../../services/performance';
import { withBackendContext } from '../../hotels-utils/components-hotel-card-adjacent/backend-context';
import { stayLength } from '../../hotels-utils/date-utils';
import { FormattedMessage, withI18n } from '../../hotels-utils/services/i18n';
import { getPricePolicyText } from '../../hotels-utils/services/price-policy/price-policy';
import complyRegulator from '../../hotels-utils/services/regulator';
import { getCugOverrideFromUrl } from '../../hotels-utils/services/url/url';
import { withConfiguration } from '../../hotels-utils/skyscanner-application/configuration';
import AISummary from '../AISummary';
import Distance from '../Distance';
import ReviewRating from '../ReviewRating';

import CardImage from './CardImage';
import CardImagesSlider from './CardImagesSlider';
import CardLayout from './CardLayout';
import CardLayoutMobile from './CardLayoutMobile';
import CardMultiImage from './CardMultiImage';
import CardRowLayout from './CardRowLayout';
import ConfidentMessage from './ConfidentMessage';
import MainPriceMobile from './MainPriceMobile';
import MainRate from './MainRate';
import MainRateWithReview from './MainRate/MainRateWithReview';
import NameStars from './NameStars';
import OtherRatesRow from './OtherRatesRow';
import PriceSummary from './PriceSummary';

import type { RecommendPriceInfoShape } from '../../hotels-utils/types';
import type { HotelCardProps } from '@skyscanner-internal/landing-pages-shared-types/types-shared/hotels-components/HotelCard';
import type { Price } from '@skyscanner-internal/landing-pages-shared-types/types-shared/hotels-components/types';

import STYLES from './HotelCard.module.scss';

const HOTEL_NOT_AVAILABLE_ERROR = 'HOTEL_NOT_AVAILABLE';
const IMAGE_RADIO_ACCESSIBILITY = '34%';
const IMAGE_RADIO_SHORT = '27.5%';

class HotelCard extends Component<HotelCardProps> {
  static defaultProps = {
    image: undefined,
    isMobile: false,
    fromSeoPage: undefined,
    onCardClicked: undefined,
    reviewsSummaryInfo: undefined,
    AISummaryInfo: undefined,
    distanceInfo: undefined,
    numberOfStars: undefined,
    error: undefined,
    hotelCardAccessibility: false,
    urlForDV: undefined,
    selected: false,
    loading: false,
    numberOfOffers: undefined,
    funnelType: undefined,
    priceType: undefined,
    cug: undefined,
    price: undefined,
    images: undefined,
    reviewForConfidentMsgList: undefined,
    allPricesInfo: undefined,
    partnerId: undefined,
    pricePolicy: undefined,
    totalImages: undefined,
    removePartnerRedirectLinks: false,
    // amenities: undefined,
    // location: undefined,
    // reviews: undefined,
    // priceInfo: undefined,
    // device: false,
  };

  // componentDidMount() {
  //   mark('dayview_firstRender', true);
  // }

  shouldComponentUpdate(nextProps: HotelCardProps) {
    return !isEqual(this.props, nextProps);
  }

  render() {
    const {
      AISummaryInfo,
      allPricesInfo,
      clientStrings,
      configs,
      cug,
      distanceInfo,
      error,
      fromSeoPage,
      funnelType,
      hotelCardAccessibility,
      i18n,
      id,
      image,
      images,
      index,
      isMobile,
      loading,
      mbdCtaTracking,
      name,
      numberOfOffers,
      numberOfStars,
      onCardClicked,
      partnerId,
      position,
      price,
      pricePolicy,
      priceType,
      removePartnerRedirectLinks,
      reviewForConfidentMsgList,
      reviewsSummaryInfo: {
        mostPopularWith,
        reviewSummaryCount,
        reviewSummaryScore,
        reviewSummaryScoreImageUrl: reviewSummaryImage,
      } = {},
      showReviewImage,
      stay,
      totalImages,
      url,
      urlForDV,
      urlNoFollow,
    } = this.props;
    const originalPrice = (cug && cug.originalPrice) || null;
    const { hotelCardAddPartnerLogo, hotelCardShortImage } = configs;

    const prices = get(allPricesInfo, 'prices', []);
    const { checkIn, checkOut } = stay;

    const goToDVEnabled = !!(fromSeoPage && !isMobile);

    const detailsUrl = () => {
      const linkUrl = goToDVEnabled && urlForDV ? urlForDV : url;
      if (typeof window === 'undefined') {
        // This happens server-side, which is fine, as there is no need to pass on
        // CUG override in this context
        return linkUrl;
      }

      const cugOverride = getCugOverrideFromUrl(window.location.href);

      if (originalPrice && cugOverride) {
        // Modify URL query params via URL interface
        const urlObject = new URL(linkUrl);
        urlObject.searchParams.set('cugOverride', cugOverride);
        return urlObject.href;
      }

      return linkUrl;
    };

    const linkRel = urlNoFollow ? 'nofollow' : undefined;

    const isFrench = complyRegulator(i18n.culture, 'FR');

    const confidentMsgNode = () => {
      if (
        !reviewForConfidentMsgList ||
        reviewForConfidentMsgList.length === 0 ||
        !reviewSummaryCount ||
        isMobile
      ) {
        return null;
      }

      return (
        <ConfidentMessage
          reviewForConfidentMsgList={reviewForConfidentMsgList}
          numberOfReviews={reviewSummaryCount}
        />
      );
    };

    const trackSearchResultSelected = (
      isMainPrice: boolean = true,
      rate?: RecommendPriceInfoShape,
      isGoToHD: boolean = true,
      mainPriceArea?: string,
    ) => {
      if (onCardClicked) {
        onCardClicked({
          id,
          cug: isMainPrice ? cug : null,
          position,
          price: isMainPrice ? price : rate?.price,
          funnelType: isMainPrice ? funnelType : rate?.funnelType,
          partnerId: isMainPrice ? partnerId : rate?.partnerId,
          isMainPrice,
          isGoToHD: isGoToHD && !goToDVEnabled,
          mainPriceArea,
          stars: numberOfStars,
          goToDVEnabled,
          viewedHotelsInfo: {
            name,
            image,
            images,
            distance: distanceInfo?.distanceMeters,
            distanceInfo,
            reviewsSummaryInfo: this.props.reviewsSummaryInfo,
            reviewForConfidentMsgList,
            url,
          },
        });
      }
    };

    const getImageNode = (type?: string) =>
      !isMobile ? (
        <CardMultiImage
          className={STYLES.HotelCard__img}
          images={images}
          totalImages={totalImages}
          mouseEnter={type === 'enter'}
          onClick={trackSearchResultSelected}
          isFrench={isFrench}
          url={detailsUrl()}
        />
      ) : (
        <CardImage
          className={classnames(
            STYLES.HotelCard__img,
            STYLES.HotelCard__imgOnMapCard,
          )}
          image={image}
          name={name}
        />
      );

    const nameVariantNode = (
      <BpkText
        textStyle={TEXT_STYLES.subheading}
        className={STYLES.HotelCard__nameVariant}
        data-test-id="hotel-name"
      >
        {name}
      </BpkText>
    );

    const starsNode = numberOfStars ? (
      <HotelStars stars={numberOfStars} index={index} />
    ) : undefined;

    const priceNode = (
      <div className={STYLES.HotelCard__price}>
        {price && i18n.formatCurrency(price)}
      </div>
    );

    const getPriceVariantNode = () => {
      const defaultNode = (
        <div className={STYLES.HotelCard__priceVariant}>
          {loading && (
            <BpkSpinner
              type={SPINNER_TYPES.primary}
              className={STYLES.HotelCard__priceNodeSpinner}
            />
          )}
          <div>
            <BpkText
              color={TEXT_COLORS.textSecondary}
              className={STYLES.HotelCard__fromPrice}
            >
              {price && i18n.translate('hotel_card_price_from')}
            </BpkText>
          </div>
          <div>
            <BpkText textStyle={TEXT_STYLES.heading2}>
              {price && i18n.formatCurrency(price)}
            </BpkText>
          </div>
        </div>
      );

      if (showReviewImage) {
        return (
          <div className={STYLES.HotelCard__priceVariantWithReview}>
            {loading && (
              <BpkSpinner
                type={SPINNER_TYPES.primary}
                className={STYLES.HotelCard__priceNodeSpinner}
              />
            )}
            <div className={STYLES.HotelCard__fromPriceRow}>
              <BpkText
                color={TEXT_COLORS.textSecondary}
                className={STYLES.HotelCard__fromPrice}
              >
                {price && i18n.translate('hotel_card_price_from')}
              </BpkText>
            </div>
            <div>
              <BpkText textStyle={TEXT_STYLES.heading2}>
                {price && i18n.formatCurrency(price)}
              </BpkText>
            </div>
          </div>
        );
      }

      return defaultNode;
    };

    const priceVariantNode = getPriceVariantNode();

    const isDefaultPerNight =
      priceType === HOTELS_PRICE_TYPE.PRICE_TYPE_PER_NIGHT;

    const getNightsNum = () => {
      let numberOfNights = stayLength({ checkIn, checkOut });
      if (isDefaultPerNight) {
        numberOfNights = 1;
      }
      return numberOfNights;
    };

    const nightNumNode = () => {
      const numberOfNights = getNightsNum();

      const numLabel = (
        <FormattedMessage
          label="Price_label_forNights"
          cardinality={numberOfNights}
          data={{ num: numberOfNights }}
        />
      );

      return (
        <BpkText
          tagName="p"
          color={TEXT_COLORS.textOnDark}
          textStyle={TEXT_STYLES.caption}
          className={STYLES.HotelCard__nightNum}
        >
          {numLabel}
        </BpkText>
      );
    };

    const buildOriginalPriceNode = () => (
      <div className={STYLES.HotelCard__priceOriginal}>
        {originalPrice && i18n.formatCurrency(originalPrice)}
      </div>
    );

    const FSSNode = cug ? (
      <CugBadge
        discount={cug.discountPercentage}
        type={cug.type}
        isMobile={isMobile}
      />
    ) : null;

    const buildFSSBadge = () => {
      if (cug) {
        return (
          <div className={STYLES.HotelCard__priceAndFss}>
            {FSSNode}
            <div className={STYLES.HotelCard__fromPrice}>
              {i18n.translate('hotel_card_price_from')}
            </div>
            <div className={STYLES.HotelCard__allPrice}>
              {originalPrice && (
                <div className={STYLES.HotelCard__originalPrice}>
                  {i18n.formatCurrency(originalPrice)}
                </div>
              )}
              <BpkText
                textStyle={TEXT_STYLES.heading2}
                className={STYLES.HotelCard__mainPrice}
              >
                {price && i18n.formatCurrency(price)}
              </BpkText>
            </div>
          </div>
        );
      }
      return null;
    };

    const buildReviewSummary = () => {
      if (reviewSummaryScore && reviewSummaryCount) {
        return (
          <ReviewRating
            score={reviewSummaryScore}
            count={reviewSummaryCount}
            image={reviewSummaryImage}
            className={STYLES.HotelCard__reviewRating}
            showReviewImage={showReviewImage}
          />
        );
      }
      return null;
    };

    const buildAISummary = () => <AISummary {...AISummaryInfo} />;
    const buildPriceAndNumbers = () => {
      if (!numberOfOffers && loading) {
        return <BpkSpinner type={SPINNER_TYPES.primary} />;
      }

      if (error === HOTEL_NOT_AVAILABLE_ERROR) {
        return (
          <div className={STYLES.HotelCard__unavailable}>
            {i18n.translate('HotelCard_label_hotelUnavailable')}
          </div>
        );
      }

      if (originalPrice) {
        return (
          <>
            {FSSNode}
            <div>
              <BpkText
                color={TEXT_COLORS.textSecondary}
                className={STYLES.HotelCard__fromPrice}
              >
                {price && i18n.translate('hotel_card_price_from')}
              </BpkText>
            </div>
            <div className={STYLES.HotelCard__priceAll}>
              {buildOriginalPriceNode()}
              {priceNode}
            </div>
            {nightNumNode()}
          </>
        );
      }

      return (
        <>
          <div className={STYLES.HotelCard__fromPrice}>
            {i18n.translate('hotel_card_price_from')}
          </div>
          {numberOfOffers ? priceNode : null}
          {nightNumNode()}
        </>
      );
    };

    const seeDetailsNode =
      error === HOTEL_NOT_AVAILABLE_ERROR ? null : (
        <div className={STYLES.HotelCard__cta}>
          <BpkLoadingButton
            data-tracking-element-id={mbdCtaTracking?.elementId}
            data-tracking-common-params={mbdCtaTracking?.commonParams}
          >
            {goToDVEnabled
              ? clientStrings.hotelCardCTALabelForDV
              : clientStrings.hotelCardCTALabel}
          </BpkLoadingButton>
        </div>
      );

    const distanceVariantNode = distanceInfo ? (
      <div
        className={classnames(STYLES.HotelCard__distanceAndIcon, {
          [STYLES.HotelCard__distanceAndIconNoIcon]: showReviewImage,
        })}
      >
        {!showReviewImage && (
          <div className={STYLES.HotelCard__distanceIcon}>
            <LocationIconSM />
          </div>
        )}
        <BpkText
          tagName="p"
          textStyle={TEXT_STYLES.bodyDefault}
          className={STYLES.HotelCard__distanceVariant}
        >
          <Distance distance={distanceInfo} isMobile={isMobile} />
        </BpkText>
      </div>
    ) : null;

    const getOriginalPrice = () => {
      if (originalPrice) {
        return i18n.formatCurrency(originalPrice);
      }

      return undefined;
    };

    const getLogoNode = (allPrices: Price[]) => {
      if (!allPrices || !allPrices.length) return null;

      const [mainPrice] = allPrices;
      const { logoLink, name: partnerName } = mainPrice;
      return hotelCardAddPartnerLogo ? (
        <OTALogo src={logoLink} name={partnerName} height={24} index={index} />
      ) : undefined;
    };
    const buildBadgesForMobile = (allPrices: Price[]) => {
      if (!allPrices || !allPrices.length) return null;

      if (cug) {
        return (
          <div className={STYLES.HotelCard__badgeAndOriginalPrice}>
            <CugBadge
              discount={cug.discountPercentage}
              type={cug.type}
              isMobile={isMobile}
              className={STYLES.HotelCard__fssBadge}
            />
          </div>
        );
      }

      return null;
    };

    const getGuestTypeNode = () => {
      if (!mostPopularWith) {
        return null;
      }

      const guestType = i18n.translate(
        `Travellertype_${mostPopularWith}_lowercase`,
      );
      return (
        <BpkBadge type={BADGE_TYPES.normal}>
          <FormattedMessage
            label="HotelCard_label_greatForActivityOrGuestType"
            data={{ activity_or_guest_type: guestType }}
          />
        </BpkBadge>
      );
    };

    const getOtherPrices = () => {
      if (removePartnerRedirectLinks) {
        return null;
      }

      if (!numberOfOffers && loading) {
        return null;
      }

      if (error === HOTEL_NOT_AVAILABLE_ERROR) {
        return null;
      }

      const [, ...otherRates] = prices || [];
      if (!otherRates || otherRates.length === 0) {
        return null;
      }
      if (!isMobile) {
        return (
          <OtherRatesRow
            otherRates={otherRates}
            onClick={trackSearchResultSelected}
            detailUrl={detailsUrl()}
          />
        );
      }
      return null;
    };

    const getUrl = () => {
      if (!numberOfOffers && loading) {
        return <BpkSpinner type={SPINNER_TYPES.primary} />;
      }

      if (error === HOTEL_NOT_AVAILABLE_ERROR) {
        return (
          <div className={STYLES.HotelCard__unavailable}>
            {i18n.translate('HotelCard_label_hotelUnavailable')}
          </div>
        );
      }
      const [lowestPrice] = prices;
      let link = detailsUrl();

      if (!goToDVEnabled && lowestPrice.funnelType === 'meta') {
        link = lowestPrice.deepLink;
      }

      return link;
    };

    const buildStayPricesSummary = () => {
      if (!prices || !prices.length) {
        return null;
      }

      // For SEO pages we've decided not to show 'total stay' price
      // so we are not passing price/priceType/additionalPrice props here
      return <PriceSummary />;
    };

    const pricePolicyNode =
      error === HOTEL_NOT_AVAILABLE_ERROR ? null : (
        <BpkText
          tagName="p"
          color={TEXT_COLORS.textOnDark}
          textStyle={TEXT_STYLES.footnote}
          className={STYLES.HotelCard__label}
        >
          {pricePolicy && getPricePolicyText(pricePolicy, i18n)}
        </BpkText>
      );

    const distanceNode = distanceInfo ? (
      <Distance distance={distanceInfo} isMobile={isMobile} />
    ) : null;
    const ratingNode =
      reviewSummaryScore && reviewSummaryCount ? (
        <ReviewRating
          score={reviewSummaryScore}
          count={reviewSummaryCount}
          image={reviewSummaryImage}
          index={index}
          showReviewImage={showReviewImage}
        />
      ) : null;

    const detailUrl = detailsUrl();
    let imageRadio;
    if (hotelCardAccessibility) {
      if (hotelCardShortImage) {
        imageRadio = IMAGE_RADIO_SHORT;
      } else {
        imageRadio = IMAGE_RADIO_ACCESSIBILITY;
      }
    } else {
      imageRadio = undefined;
    }
    const imageSlider = (
      <CardImagesSlider
        images={images}
        hotelName={name}
        stars={starsNode}
        detailUrl={detailUrl}
        detailUrlRel={linkRel}
        onClick={trackSearchResultSelected}
        imageRadio={imageRadio}
        hotelCardAccessibility={hotelCardAccessibility}
      />
    );
    const nameStarNode = (
      <NameStars
        hotelName={name}
        stars={starsNode}
        className={STYLES.HotelCard__nameStars}
        hotelCardAccessibility={hotelCardAccessibility}
        index={index}
      />
    );

    const mobilePriceBadge = buildBadgesForMobile(prices);
    const logoNode = getLogoNode(prices);

    const hasMobilePriceBadge = !!mobilePriceBadge;
    const distanceForMobile = distanceInfo ? (
      <div
        className={classnames(STYLES.HotelCard__distanceAndIcon, {
          [STYLES.HotelCard__distanceAndIconPart]: hasMobilePriceBadge,
          [STYLES.HotelCard__distanceAndIconAll]: !hasMobilePriceBadge,
        })}
      >
        {!showReviewImage && (
          <div className={STYLES.HotelCard__distanceIconMobile}>
            <LocationIconSM />
          </div>
        )}
        <BpkText
          tagName="p"
          color={TEXT_COLORS.textSecondary}
          textStyle={TEXT_STYLES.caption}
          className={STYLES.HotelCard__distanceVariantMobile}
        >
          {distanceNode}
        </BpkText>
      </div>
    ) : null;

    const getMainPrice = () => {
      if (!numberOfOffers && loading) {
        return (
          <BpkSpinner
            type={SPINNER_TYPES.primary}
            className={STYLES.HotelCard__priceSpinner}
          />
        );
      }

      if (error === HOTEL_NOT_AVAILABLE_ERROR || !prices || !prices.length) {
        return (
          <BpkCard
            className={STYLES.HotelCard__noPrice}
            data-tracking-element-id={mbdCtaTracking?.elementId}
            data-tracking-common-params={mbdCtaTracking?.commonParams}
            onClick={() =>
              trackSearchResultSelected(
                true,
                undefined,
                isMobile ? true : funnelType === 'dbook',
                'stub',
              )
            }
            target="_blank"
            href={detailsUrl()}
            rel={linkRel}
            padded={false}
          >
            <div className={STYLES.HotelCard__unavailable}>
              {i18n.translate('HotelCard_label_hotelUnavailable')}
            </div>
          </BpkCard>
        );
      }

      const [mainPrice] = prices;
      const formattedOriginalPrice = getOriginalPrice();

      if (isMobile) {
        return (
          <MainPriceMobile
            mainPrice={mainPrice}
            isDefaultPerNight={isDefaultPerNight}
            originalNights={stayLength({ checkIn, checkOut })}
            price={price}
            priceBadge={mobilePriceBadge}
            originalPrice={formattedOriginalPrice}
            distance={distanceForMobile}
            loading={loading}
            hotelCardAccessibility={hotelCardAccessibility}
            index={index}
            logoNode={logoNode}
            showDivider={hotelCardShortImage}
          />
        );
      }

      const MainRateComponent = showReviewImage ? MainRateWithReview : MainRate;

      return (
        <MainRateComponent
          mainPrice={mainPrice}
          numberOfNights={getNightsNum()}
          priceNode={priceVariantNode}
          fssBadge={buildFSSBadge()}
          onClick={trackSearchResultSelected}
          // TODO: [JIRA-XXXX] Fix url type, expect string but getUrl() can return an Element
          // @ts-expect-error -- legacy code
          url={getUrl()}
          pricePolicy={pricePolicyNode}
          clientStrings={clientStrings}
          stayPricesSummary={buildStayPricesSummary()}
          detailsUrl={detailsUrl()}
          goToDVEnabled={goToDVEnabled}
          mbdTracking={mbdCtaTracking}
        />
      );
    };

    if (!isFrench && !isMobile) {
      return (
        <CardRowLayout
          name={nameVariantNode}
          stars={starsNode}
          badges={getGuestTypeNode()}
          mainPrice={getMainPrice()}
          otherPrices={getOtherPrices()}
          distance={distanceVariantNode}
          reviews={buildReviewSummary()}
          AISummary={buildAISummary()}
          confidentMsg={confidentMsgNode()}
          onClick={trackSearchResultSelected}
          getImageNode={getImageNode}
          showReviewImage={showReviewImage}
          url={detailsUrl()}
        />
      );
    }

    if (isMobile) {
      return (
        <CardLayoutMobile
          imageSlider={imageSlider}
          priceInfo={getMainPrice()}
          url={detailsUrl()}
          urlRel={linkRel}
          onClick={trackSearchResultSelected}
          ratingNode={ratingNode}
          AISummary={buildAISummary()}
          confidentMsg={confidentMsgNode()}
          distanceNode={hotelCardAccessibility ? distanceNode : null}
          imageRadio={imageRadio}
          hotelCardAccessibility={hotelCardAccessibility}
          nameStarNode={hotelCardAccessibility && nameStarNode}
          index={index}
        />
      );
    }

    return (
      <CardLayout
        name={nameVariantNode}
        stars={starsNode}
        badges={getGuestTypeNode()}
        priceAndNumbers={buildPriceAndNumbers()}
        distance={distanceVariantNode}
        seeDetails={seeDetailsNode}
        url={detailsUrl()}
        onClick={trackSearchResultSelected}
        pricePolicy={pricePolicyNode}
        stayPricesSummary={buildStayPricesSummary()}
        reviewSummary={buildReviewSummary()}
        AISummary={buildAISummary()}
        getImageNode={getImageNode}
        confidentMsg={confidentMsgNode()}
      />
    );
  }
}

export default withBackendContext(withConfiguration(withI18n(HotelCard)));
